"use client";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronRightIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import {
	claimExclusiveOverlay,
	getExclusiveOverlayActiveId,
	releaseExclusiveOverlay,
	subscribeExclusiveOverlay,
} from "../lib/exclusive-overlay";
import { MenuCircularRadioIndicator } from "./circular-field-control";

type DropdownMenuProps = React.ComponentProps<
	typeof DropdownMenuPrimitive.Root
> & {
	/**
	 * When true, opening claims the shared **dropdown** exclusive slot (closes
	 * sibling exclusive menus only — ContentTooltips stay independent).
	 * Opt-in for top-level chrome (UserMenu / OrganizationSelect). Nested
	 * toggles like ColorMode must leave this false.
	 */
	exclusive?: boolean;
};

/**
 * DropdownMenu root. Pass `exclusive` for app-chrome menus that must not stack
 * with each other. Tips use a separate singleton and may stay open alongside.
 */
function DropdownMenu({
	open: openProp,
	defaultOpen,
	onOpenChange,
	exclusive = false,
	...props
}: DropdownMenuProps) {
	const id = React.useId();
	const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
		defaultOpen ?? false,
	);
	const isControlled = openProp !== undefined;
	const open = isControlled ? Boolean(openProp) : uncontrolledOpen;
	const onOpenChangeRef = React.useRef(onOpenChange);
	onOpenChangeRef.current = onOpenChange;

	const setOpen = React.useCallback(
		(next: boolean) => {
			if (exclusive) {
				if (next) {
					claimExclusiveOverlay(id);
				} else {
					releaseExclusiveOverlay(id);
				}
			}
			if (!isControlled) {
				setUncontrolledOpen(next);
			}
			onOpenChangeRef.current?.(next);
		},
		[exclusive, id, isControlled],
	);

	React.useEffect(() => {
		if (!exclusive) {
			return;
		}
		return subscribeExclusiveOverlay(() => {
			if (getExclusiveOverlayActiveId() === id) {
				return;
			}
			if (!open) {
				return;
			}
			if (!isControlled) {
				setUncontrolledOpen(false);
			}
			onOpenChangeRef.current?.(false);
		});
	}, [exclusive, id, open, isControlled]);

	React.useEffect(() => {
		if (!exclusive || !open) {
			return;
		}
		claimExclusiveOverlay(id);
		return () => {
			releaseExclusiveOverlay(id);
		};
	}, [exclusive, open, id]);

	return (
		<DropdownMenuPrimitive.Root
			{...(exclusive || isControlled
				? { open, onOpenChange: setOpen }
				: { defaultOpen, onOpenChange })}
			{...props}
		/>
	);
}

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
		inset?: boolean;
	}
>(({ className, inset, children, ...props }, ref) => (
	<DropdownMenuPrimitive.SubTrigger
		ref={ref}
		className={cn(
			"flex cursor-default select-none items-center rounded-lg px-3 py-1.5 text-sm outline-hidden focus:bg-accent data-[state=open]:bg-accent",
			inset ? "pl-8" : "",
			className,
		)}
		{...props}
	>
		{children}
		<ChevronRightIcon className="ml-auto size-4" />
	</DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
	DropdownMenuPrimitive.SubTrigger.displayName ?? "DropdownMenuSubTrigger";

const DropdownMenuSubContent = ({
	className,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) => (
	<DropdownMenuPrimitive.SubContent
		data-sq-overlay-dismiss-shield="dropdown-menu-sub"
		className={cn(
			"data-[state=closed]:fade-out-0 border data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[200] min-w-[8rem] overflow-hidden rounded-lg bg-popover p-1 text-popover-foreground shadow-lg shadow-black/3 data-[state=closed]:animate-out data-[state=open]:animate-in",
			className,
		)}
		{...props}
	/>
);

const DropdownMenuContent = ({
	className,
	sideOffset = 4,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) => (
	<DropdownMenuPrimitive.Portal>
		<DropdownMenuPrimitive.Content
			data-sq-overlay-dismiss-shield="dropdown-menu"
			sideOffset={sideOffset}
			className={cn(
				"z-[200] min-w-[8rem] overflow-hidden border rounded-xl bg-popover p-1 text-popover-foreground shadow-xl shadow-black/3",
				"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=open]:animate-in",
				className,
			)}
			{...props}
		/>
	</DropdownMenuPrimitive.Portal>
);

const DropdownMenuItem = ({
	className,
	inset,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
	inset?: boolean;
}) => (
	<DropdownMenuPrimitive.Item
		className={cn(
			"relative flex cursor-default select-none items-center rounded-md px-3 py-2 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
			inset ? "pl-8" : "",
			className,
		)}
		{...props}
	/>
);

const DropdownMenuCheckboxItem = ({
	className,
	children,
	checked,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) => (
	<DropdownMenuPrimitive.CheckboxItem
		className={cn(
			"relative flex cursor-default select-none items-center rounded-md py-3 pr-3 pl-8 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
			className,
		)}
		checked={checked}
		{...props}
	>
		<span className="absolute left-2 flex size-3.5 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<CheckIcon className="size-4" />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.CheckboxItem>
);

const DropdownMenuRadioItem = ({
	className,
	children,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) => (
	<DropdownMenuPrimitive.RadioItem
		className={cn(
			"relative flex cursor-default select-none items-center rounded-md py-2 pr-10 pl-3 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-[state=checked]:font-semibold data-disabled:opacity-50",
			className,
		)}
		{...props}
	>
		<span className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<MenuCircularRadioIndicator />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.RadioItem>
);

const DropdownMenuLabel = ({
	className,
	inset,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
	inset?: boolean;
}) => (
	<DropdownMenuPrimitive.Label
		className={cn(
			"px-3 py-2 font-semibold text-sm",
			inset ? "pl-8" : "",
			className,
		)}
		{...props}
	/>
);

const DropdownMenuSeparator = ({
	className,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
	<DropdownMenuPrimitive.Separator
		className={cn("-mx-1 my-1 h-px bg-border", className)}
		{...props}
	/>
);

const DropdownMenuShortcut = ({
	className,
	...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
	return (
		<span
			className={cn(
				"ml-auto text-xs tracking-widest opacity-60",
				className,
			)}
			{...props}
		/>
	);
};

export {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
};
